
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCartStore } from "@/store/cart";
import { CepInput } from "@/components/checkout/CepInput";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { calcularFrete, CepResponse, ShippingOption } from "@/services/cep";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Shirt, ChevronLeft, Trash2, Plus, Minus } from "lucide-react";
import { Link } from "react-router-dom";

export default function Checkout() {
  const navigate = useNavigate();
  const { 
    items, 
    removeItem, 
    updateQuantity, 
    getSubtotal,
    getTotal,
    shippingAddress,
    setShippingAddress,
    selectedShipping,
    setSelectedShipping
  } = useCartStore();
  
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);

  const handleAddressFound = (address: CepResponse) => {
    // Save the address to the cart store
    setShippingAddress({
      cep: address.cep,
      street: address.logradouro,
      number: "",
      complement: address.complemento,
      neighborhood: address.bairro,
      city: address.localidade,
      state: address.uf
    });
    
    // Calculate shipping options based on the CEP
    const options = calcularFrete(address.cep, items);
    setShippingOptions(options);
    
    // Default to the first shipping option if none is selected
    if (!selectedShipping && options.length > 0) {
      setSelectedShipping(options[0]);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="py-4 px-6 bg-background border-b flex items-center gap-2">
        <Link to="/shop" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft className="h-4 w-4" />
          <span>Voltar às compras</span>
        </Link>
        <div className="mx-auto flex items-center gap-2">
          <div className="bg-primary p-1.5 rounded text-primary-foreground">
            <Shirt className="h-5 w-5" />
          </div>
          <h1 className="font-semibold text-lg">Ink Shop</h1>
        </div>
      </header>
      
      <main className="container py-8 max-w-6xl">
        <h1 className="text-3xl font-bold mb-8">Finalizar Compra</h1>
        
        {items.length === 0 ? (
          <div className="text-center py-12">
            <h2 className="text-2xl font-medium mb-4">Seu carrinho está vazio</h2>
            <p className="text-muted-foreground mb-6">Adicione produtos ao seu carrinho para continuar com a compra.</p>
            <Button asChild>
              <Link to="/shop">Ir às compras</Link>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Endereço de Entrega</CardTitle>
                  <CardDescription>
                    Informe seu CEP para calcular o frete
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <CepInput 
                    onAddressFound={handleAddressFound} 
                    defaultValue={shippingAddress?.cep}
                  />
                  
                  {shippingAddress && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="font-medium">Endereço:</p>
                        <p>{shippingAddress.street}, {shippingAddress.number}</p>
                        {shippingAddress.complement && <p>{shippingAddress.complement}</p>}
                        <p>{shippingAddress.neighborhood}</p>
                        <p>{shippingAddress.city}, {shippingAddress.state}</p>
                        <p>{shippingAddress.cep}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {shippingOptions.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Opções de Entrega</CardTitle>
                    <CardDescription>
                      Escolha como deseja receber seu pedido
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {shippingOptions.map((option) => (
                        <div 
                          key={option.id}
                          className={`
                            p-4 border rounded-lg cursor-pointer transition-colors
                            ${selectedShipping?.id === option.id 
                              ? 'border-primary bg-primary/5' 
                              : 'hover:bg-muted/50'
                            }
                          `}
                          onClick={() => setSelectedShipping(option)}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <h3 className="font-medium">{option.name}</h3>
                              <p className="text-sm text-muted-foreground">
                                {option.estimatedDelivery}
                              </p>
                            </div>
                            <div className="text-right">
                              {option.price === 0 ? (
                                <span className="text-green-600 font-medium">Grátis</span>
                              ) : (
                                <span className="font-medium">R$ {option.price.toFixed(2)}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Resumo do Pedido</CardTitle>
                  <CardDescription>
                    {items.length} {items.length === 1 ? 'item' : 'itens'} no carrinho
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {items.map((item) => (
                      <div key={item.id} className="flex gap-3">
                        <div className="h-16 w-16 rounded-md overflow-hidden flex-shrink-0">
                          <img 
                            src={item.imageUrl} 
                            alt={item.title} 
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-sm line-clamp-1">{item.title}</h4>
                          <div className="mt-1 flex justify-between">
                            <div className="flex items-center border rounded-md">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6 rounded-none"
                                onClick={() => updateQuantity(item.id, (item.quantity || 1) - 1)}
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-6 text-center text-xs">{item.quantity}</span>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6 rounded-none"
                                onClick={() => updateQuantity(item.id, (item.quantity || 1) + 1)}
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                            <div className="text-xs flex gap-1.5 items-center">
                              {item.discountedPrice ? (
                                <span>R$ {(item.discountedPrice * (item.quantity || 1)).toFixed(2)}</span>
                              ) : (
                                <span>R$ {(item.price * (item.quantity || 1)).toFixed(2)}</span>
                              )}
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-5 w-5 rounded-full"
                                onClick={() => removeItem(item.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <Separator className="my-4" />
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>R$ {getSubtotal().toFixed(2)}</span>
                    </div>
                    {selectedShipping && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Frete ({selectedShipping.name})
                        </span>
                        <span>
                          {selectedShipping.price === 0 
                            ? 'Grátis'
                            : `R$ ${selectedShipping.price.toFixed(2)}`
                          }
                        </span>
                      </div>
                    )}
                    <Separator className="my-2" />
                    <div className="flex justify-between font-bold">
                      <span>Total</span>
                      <span>R$ {getTotal().toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button className="w-full" disabled={!selectedShipping}>
                    Continuar para pagamento
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
